import { useState, useEffect } from 'react';
import { api, useToast, PanelHowTo } from './_barrel.js';


        // ====================================================================
        // SubscriptionPanel — monitor this Subject/Business on a schedule.
        // Used on both Subject Detail (Details tab) and Business Detail.
        // ====================================================================
        function SubscriptionPanel({ token, targetKind, targetId }) {
            const [sub, setSub] = useState(null);     // existing subscription if any
            const [loaded, setLoaded] = useState(false);
            const [editing, setEditing] = useState(false);
            const [frequency, setFrequency] = useState('daily');
            const [emailsText, setEmailsText] = useState('');
            const [runHourUtc, setRunHourUtc] = useState(7);
            const [runTimezone, setRunTimezone] = useState('Europe/London');
            const [notes, setNotes] = useState('');
            const [error, setError] = useState(null);
            const [saving, setSaving] = useState(false);
            const [refresh, setRefresh] = useState(0);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const path = targetKind === 'subject'
                            ? `/api/v1/subscriptions/for-subject/${targetId}`
                            : `/api/v1/subscriptions/for-business/${targetId}`;
                        const r = await api(path, {}, token);
                        setSub(r);
                        if (r) {
                            setFrequency(r.frequency);
                            setEmailsText((r.alert_emails || []).join(', '));
                            setNotes(r.notes || '');
                            setRunHourUtc(r.run_hour_utc !== null && r.run_hour_utc !== undefined ? r.run_hour_utc : 7);
                            setRunTimezone(r.run_timezone || 'Europe/London');
                        }
                    } catch (e) { setError(e.message); }
                    finally { setLoaded(true); }
                })();
            }, [token, targetKind, targetId, refresh]);

            const save = async () => {
                setSaving(true); setError(null);
                const emails = emailsText.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
                try {
                    if (sub) {
                        // PATCH existing
                        await api(`/api/v1/subscriptions/${sub.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ frequency, alert_emails: emails, notes: notes || null, run_hour_utc: runHourUtc, run_timezone: runTimezone }),
                        }, token);
                        toast('Subscription updated');
                    } else {
                        // POST new
                        await api('/api/v1/subscriptions', {
                            method: 'POST',
                            body: JSON.stringify({
                                [targetKind === 'subject' ? 'subject_id' : 'business_id']: targetId,
                                frequency,
                                alert_emails: emails,
                                notes: notes || null,
                            }),
                        }, token);
                        toast('Monitoring started');
                    }
                    setEditing(false);
                    setRefresh(r => r+1);
                } catch (e) { setError(e.message); }
                finally { setSaving(false); }
            };

            const togglePause = async () => {
                if (!sub) return;
                try {
                    const path = sub.paused_at ? 'resume' : 'pause';
                    await api(`/api/v1/subscriptions/${sub.id}/${path}`, { method: 'POST' }, token);
                    toast(sub.paused_at ? 'Subscription resumed' : 'Subscription paused');
                    setRefresh(r => r+1);
                } catch (e) { setError(e.message); }
            };

            const remove = async () => {
                if (!sub) return;
                if (!confirm('Stop monitoring this target? This deletes the subscription.')) return;
                try {
                    await api(`/api/v1/subscriptions/${sub.id}`, { method: 'DELETE' }, token);
                    toast('Monitoring stopped');
                    setSub(null);
                    setRefresh(r => r+1);
                } catch (e) { setError(e.message); }
            };

            if (!loaded) return null;

            const freqLabel = f => ({
                daily: 'Daily',
                weekly: 'Weekly',
                on_change_only: 'On change only (manual trigger)',
            })[f] || f;

            return (
                <div className="panel mb-6">
                    <div className="panel-header flex justify-between items-center">
                        <div>
                            <div className="font-display text-xl gold">Monitoring</div>
                            <div className="text-xs font-mono text-muted mt-1">
                                {sub ? 'Scheduled monitoring active' : 'Not currently monitored on a schedule'}
                            </div>
                        </div>
                        {sub && !editing && (
                            <div className="flex gap-2">
                                <button className="btn-ghost" onClick={togglePause}>
                                    {sub.paused_at ? 'Resume' : 'Pause'}
                                </button>
                                <button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>
                                <button className="btn-ghost" style={{color: 'var(--red)'}} onClick={remove}>Stop</button>
                            </div>
                        )}
                    </div>
                    <PanelHowTo id="monitoring" />

                    {error && <div className="px-6 py-3 text-sm" style={{color:'var(--red)'}}>{error}</div>}

                    {!sub && !editing && (
                        <div className="px-6 py-5">
                            <button className="btn-gold" onClick={() => setEditing(true)}>
                                + Start monitoring
                            </button>
                            <div className="text-xs text-muted mt-3">
                                Schedule recurring collection. Alerts go to the configured emails
                                (or the tenant intake address if left blank).
                            </div>
                        </div>
                    )}

                    {sub && !editing && (
                        <div className="px-6 py-5 grid grid-cols-3 gap-6 text-sm">
                            <div>
                                <div className="text-xs text-muted uppercase tracking-widest">Frequency</div>
                                <div>{freqLabel(sub.frequency)}</div>
                                {sub.paused_at && (
                                    <div className="text-xs mt-1" style={{color: 'var(--amber)'}}>
                                        PAUSED · {new Date(sub.paused_at).toLocaleDateString('en-GB')}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="text-xs text-muted uppercase tracking-widest">Alerts go to</div>
                                <div className="font-mono text-xs">
                                    {sub.alert_emails && sub.alert_emails.length > 0
                                        ? sub.alert_emails.join(', ')
                                        : <span className="text-muted">Tenant intake address</span>}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-muted uppercase tracking-widest">Digest time</div>
                                <div className="text-xs font-mono">
                                    {String(sub.run_hour_utc ?? 7).padStart(2, '0')}:00 {sub.run_timezone || 'Europe/London'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-muted uppercase tracking-widest">Last run</div>
                                <div className="text-xs font-mono">
                                    {sub.last_run_at ? new Date(sub.last_run_at).toLocaleString('en-GB') : '—'}
                                </div>
                                {sub.next_run_at && !sub.paused_at && (
                                    <div className="text-xs text-muted mt-1">
                                        Next: {new Date(sub.next_run_at).toLocaleDateString('en-GB')}
                                    </div>
                                )}
                            </div>
                            {sub.notes && (
                                <div className="col-span-3">
                                    <div className="text-xs text-muted uppercase tracking-widest">Notes</div>
                                    <div className="text-sm">{sub.notes}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {editing && (
                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">
                                    Frequency
                                </label>
                                <select value={frequency} onChange={e => setFrequency(e.target.value)}>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="on_change_only">On change only (manual trigger)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">
                                    Digest delivery hour
                                </label>
                                <select value={runHourUtc} onChange={e => setRunHourUtc(parseInt(e.target.value, 10))}>
                                    {[5,6,7,8,9,10,11,12,17,19].map(h => (
                                        <option key={h} value={h}>
                                            {String(h).padStart(2,'0')}:00 UTC
                                            {' '}({String((h + 1) % 24).padStart(2,'0')}:00 BST in summer, {String(h % 24).padStart(2,'0')}:00 GMT in winter)
                                        </option>
                                    ))}
                                </select>
                                <div className="text-xs text-muted mt-1">
                                    The daily digest email goes out at this UTC hour.
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">
                                    Timezone reference
                                </label>
                                <select value={runTimezone} onChange={e => setRunTimezone(e.target.value)}>
                                    <option value="Europe/London">Europe/London (UK)</option>
                                    <option value="Europe/Paris">Europe/Paris (CET)</option>
                                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                                    <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                                    <option value="America/New_York">America/New_York (ET)</option>
                                </select>
                                <div className="text-xs text-muted mt-1">
                                    Reference only — actual fire time is the UTC hour above.
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">
                                    Alert emails (comma separated; blank = tenant default)
                                </label>
                                <input value={emailsText} onChange={e => setEmailsText(e.target.value)}
                                       placeholder="csit@client.example, dean@client.example" />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">
                                    Notes (optional)
                                </label>
                                <textarea rows="2" value={notes} onChange={e => setNotes(e.target.value)}
                                          placeholder="Engagement reference, special handling notes..." />
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button className="btn-ghost" onClick={() => {
                                    setEditing(false);
                                    if (sub) {
                                        setFrequency(sub.frequency);
                                        setEmailsText((sub.alert_emails || []).join(', '));
                                        setNotes(sub.notes || '');
                                    }
                                }}>Cancel</button>
                                <button className="btn-gold" onClick={save} disabled={saving}>
                                    {saving ? 'Saving…' : (sub ? 'Save changes' : 'Start monitoring')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }



export { SubscriptionPanel };
